import { useFinanceApi } from 'infrastructure/api';
import type { ApiRequestOptions } from 'framework/api';
import { ResourceData, ResourceId, useCrudService, useTimelineService } from 'framework/crud';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import createOneStopVendorValidationSchema from './createupdate/Create/createOneStopVendorValidationSchema';
import updateVendorValidationSchema from './createupdate/Update/updateVendorValidationSchema';
import { cloneDeep } from 'lodash';
import { VendorDto, VendorAgreementDto } from './types';
import complianceVendorValidationSchema from './ComplianceVendor/complianceVendorValidationSchema';
import finalizeAgreementVendorValidationSchema from './AgreementVendor/finalizeAgreementVendorValidationSchema';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { useVendorId } from './routes';
import { VENDOR_DOCUMENT_TYPES } from './VendorTabs/vendorDocuments/constants';

const RESOURCE_URL = '/vendors';

export const useVendorTimelineService = () => {
  const id = useVendorId();
  const { uploadDocument } = useDocumentStorageContext();

  const {
    search: searchTimelineItems,
    create,
    getTimelinePageById,
  } = useTimelineService({
    parentEntityUrl: '/vendors/:id',
    useService: useFinanceApi,
  });

  const createTimelineItem = async (entityId: ResourceId, timelineEntityType: string, data: ResourceData) => {
    try {
      let attachments = data.attachments || [];

      if (attachments.length) {
        const commitFiles = await uploadDocument({
          vendorId: id,
          fileType: VENDOR_DOCUMENT_TYPES.OTHER,
          paths: attachments,
        });
        attachments = commitFiles.paths || [];
      }

      return create(entityId, timelineEntityType, {
        ...data,
        attachments,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create timeline item:', error);
      throw error;
    }
  };

  return {
    searchTimelineItems,
    createTimelineItem,
    getTimelinePageById,
  };
};

const useVendorService = () => {
  const { sendPostRequest, sendPutRequest, sendGetRequest } = useFinanceApi();
  const {
    create: initCreate,
    update,
    search,
    ...baseCrud
  } = useCrudService({
    entityName: 'Vendor',
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useFinanceApi,
  });

  const searchVendors = ({ filter = {}, searchValue, ...params }: ResourceData, options?: ApiRequestOptions) => {
    const { isCompliant, ...filters } = filter;

    return search(
      {
        ...params,
        filter: {
          ...filters,
          ...(isCompliant?.eq ? { isCompliant: { eq: isCompliant.eq === 'YES' } } : {}),
          ...(searchValue ? { 'specification:vendorSearch': { eq: searchValue } } : {}),
        },
      },
      options,
    );
  };

  const create = async (data: FormFieldsData) => {
    const createDate = cloneDeep(data) as any;
    return sendPostRequest('/vendors/request', {
      ...createDate,
      publicOfferLink: createDate?.publicOfferLink,
      contractDraft: (createDate?.contractDraft || [])[0],
    });
  };

  const updateVendor = async (id: string, data: FormFieldsData) => {
    const editDate = cloneDeep(data) as VendorDto;
    return update(id, {
      ...editDate,
    });
  };

  const rejectVendor = async (id: string) => {
    return sendPostRequest(`/vendors/${id}/reject`);
  };

  const complianceVendor = async (id: string, complianceData: any) => {
    return sendPostRequest(`/vendors/${id}/compliance`, {
      ...complianceData,
      agreementDetails: {
        ...complianceData.agreementDetails,
        contractDraft: (complianceData?.agreementDetails?.contractDraft || [])[0],
      },
    });
  };

  const agreementVendor = async (id: string, agreementData: any) => {
    return sendPostRequest(`/vendors/${id}/agreement`, {
      vendorName: agreementData.vendorName,
      purpose: agreementData.purpose,
      phoneNumber: agreementData.phoneNumber,
      email: agreementData.email,
      contacts: agreementData.contacts,
      agreementDetails: {
        agreementType: agreementData.agreementDetails.agreementType,
        durationType: agreementData.agreementDetails.durationType,
        startDate: agreementData.agreementDetails.startDate,
        endDate: agreementData.agreementDetails.endDate,
        publicOfferLink: agreementData?.agreementDetails?.publicOfferLink,
        signedContractAttachment: (agreementData?.agreementDetails?.signedContractAttachment || [])[0],
      },
    });
  };

  const getVendorAgreements = async (id: string, loadParams: any) => {
    const request = await sendPostRequest(`/vendors/${id}/agreements/search`, loadParams);
    return request.json();
  };

  const createAgreement = async (vendorId: string, agreementData: Partial<VendorAgreementDto>) => {
    const processedData = {
      ...agreementData,
      signedContractAttachment: (agreementData?.signedContractAttachment || [])[0],
    };
    return sendPostRequest(`/vendors/${vendorId}/agreements`, processedData);
  };

  const finalizeAgreement = async (agreementId: string, agreementData: Partial<VendorAgreementDto>) => {
    const processedData = {
      ...agreementData,
      signedContractAttachment: (agreementData?.signedContractAttachment || [])[0],
    };
    return sendPostRequest(`/vendors/agreements/${agreementId}/finalize`, processedData);
  };

  const terminateAgreement = async (agreementId: string, agreementData: { endDate: string }) => {
    return sendPostRequest(`/vendors/agreements/${agreementId}/terminate`, {
      ...agreementData,
      id: agreementId,
    });
  };

  const updateAgreement = async (agreementId: string, agreementData: Partial<VendorAgreementDto>) => {
    const processedData = {
      ...agreementData,
      signedContractAttachment: (agreementData?.signedContractAttachment || [])[0],
      id: agreementId,
    };
    return sendPutRequest(`/vendors/agreements/${agreementId}`, processedData);
  };

  const searchVendorCostCentersAccounts = async () => {
    const response = (await sendGetRequest(`/vendors/cost-centers`)) || [];
    const result = await response.json();

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  };

  const getAgreementPermissions = async (agreementId: string) => {
    try {
      const response = await sendGetRequest(`/permissions/VendorAgreement/${agreementId}`);
      return response.json();
    } catch {
      return [];
    }
  };

  const getEditVendorValidationSchema = () => Promise.resolve(updateVendorValidationSchema);
  const createVendorValidationSchema = () => Promise.resolve(createOneStopVendorValidationSchema);
  const complianceVendorValidationSchemaFunc = () => Promise.resolve(complianceVendorValidationSchema);
  const finalizeVendorValidationSchemaFunc = () => Promise.resolve(finalizeAgreementVendorValidationSchema);

  return {
    ...baseCrud,
    create,
    search,
    searchVendors,
    update: updateVendor,
    getEditVendorValidationSchema,
    createVendorValidationSchema,
    complianceVendorValidationSchemaFunc,
    finalizeVendorValidationSchemaFunc,
    rejectVendor,
    complianceVendor,
    agreementVendor,
    getVendorAgreements,
    createAgreement,
    finalizeAgreement,
    terminateAgreement,
    updateAgreement,
    searchVendorCostCentersAccounts,
    getAgreementPermissions,
  };
};

export default useVendorService;
